import KoreanLunarCalendar from 'korean-lunar-calendar';
import { LunarYear, Solar } from 'lunar-typescript';
import type {
  Branch, CalendarDate, Element, Pillar, PillarKey, SajuInput, SajuResult,
  SajuValidationIssue, SajuWarning, SolarTerm, Stem, TenGod,
} from './types';

export type * from './types';

export const ELEMENTS: readonly Element[] = ['wood', 'fire', 'earth', 'metal', 'water'];
export const ELEMENT_LABELS: Record<Element, string> = { wood: '목', fire: '화', earth: '토', metal: '금', water: '수' };
export const SAJU_MIN_YEAR = 1900;
export const SAJU_MAX_YEAR = 2050;

const STEM_KOREAN = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const STEM_HANJA = [...'甲乙丙丁戊己庚辛壬癸'];
const BRANCH_KOREAN = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
const BRANCH_HANJA = [...'子丑寅卯辰巳午未申酉戌亥'];
const BRANCH_ELEMENTS: Element[] = ['water', 'earth', 'wood', 'wood', 'earth', 'fire', 'fire', 'earth', 'metal', 'metal', 'earth', 'water'];
const ANIMALS = ['쥐', '소', '호랑이', '토끼', '용', '뱀', '말', '양', '원숭이', '닭', '개', '돼지'];
const PILLAR_LABELS: Record<PillarKey, string> = { year: '년주', month: '월주', day: '일주', hour: '시주' };

/** Common principal-qi-first hidden-stem table; see 6tail LunarUtil.ZHI_HIDE_GAN. No day-in-month qi weighting is implied. */
const HIDDEN_STEM_INDICES = [[9], [5, 9, 7], [0, 2, 4], [1], [4, 1, 9], [2, 6, 4], [3, 5], [5, 3, 1], [6, 8, 4], [7], [4, 7, 3], [8, 0]];

// The 12 jie, in civil January-to-December order. Zhongqi do not change the month pillar.
const JIE_NAMES = ['소한', '입춘', '경칩', '청명', '입하', '망종', '소서', '입추', '백로', '한로', '입동', '대설'];
const JIE_HANJA = ['小寒', '立春', '驚蟄', '清明', '立夏', '芒種', '小暑', '立秋', '白露', '寒露', '立冬', '大雪'];
const termCache = new Map<number, SolarTerm[]>();
const mod = (value: number, divisor: number) => ((value % divisor) + divisor) % divisor;
const pad = (value: number) => String(value).padStart(2, '0');
const ymd = (date: CalendarDate) => `${date.year}-${pad(date.month)}-${pad(date.day)}`;
const localStamp = (date: CalendarDate, hour: number, minute: number, second = 0) => `${ymd(date)}T${pad(hour)}:${pad(minute)}:${pad(second)}+09:00`;

export class SajuValidationError extends Error {
  readonly errors: SajuValidationIssue[];
  constructor(errors: SajuValidationIssue[]) {
    super(errors.map(issue => issue.message).join(' '));
    this.name = 'SajuValidationError';
    this.errors = errors;
  }
}

function makeStem(index: number): Stem {
  return { index, korean: STEM_KOREAN[index], hanja: STEM_HANJA[index], element: ELEMENTS[Math.floor(index / 2)], yinYang: index % 2 === 0 ? 'yang' : 'yin' };
}

function makeBranch(index: number): Branch {
  return { index, korean: BRANCH_KOREAN[index], hanja: BRANCH_HANJA[index], element: BRANCH_ELEMENTS[index], yinYang: index % 2 === 0 ? 'yang' : 'yin', animal: ANIMALS[index] };
}

/** Relationship of a target stem to the day master. Indices are 0-based, 갑=0 ... 계=9. */
export function getTenGod(dayStemIndex: number, targetStemIndex: number): TenGod {
  if (![dayStemIndex, targetStemIndex].every(index => Number.isInteger(index) && index >= 0 && index < 10)) {
    throw new RangeError('천간 인덱스는 0부터 9 사이의 정수여야 합니다.');
  }
  const relation = mod(Math.floor(targetStemIndex / 2) - Math.floor(dayStemIndex / 2), 5);
  const samePolarity = dayStemIndex % 2 === targetStemIndex % 2;
  const table: readonly (readonly [TenGod, TenGod])[] = [
    ['비견', '겁재'], ['식신', '상관'], ['편재', '정재'], ['편관', '정관'], ['편인', '정인'],
  ];
  return table[relation][samePolarity ? 0 : 1];
}

function makePillar(key: PillarKey, stemIndex: number, branchIndex: number, dayStemIndex: number): Pillar {
  const stem = makeStem(stemIndex);
  const branch = makeBranch(branchIndex);
  const hiddenStems = HIDDEN_STEM_INDICES[branchIndex].map((index, offset) => ({
    stem: makeStem(index), tenGod: getTenGod(dayStemIndex, index), role: offset === 0 ? 'principal' as const : 'secondary' as const,
  }));
  return {
    key, label: PILLAR_LABELS[key], korean: stem.korean + branch.korean, hanja: stem.hanja + branch.hanja,
    stem, branch, tenGod: key === 'day' ? null : getTenGod(dayStemIndex, stemIndex),
    branchTenGod: hiddenStems[0].tenGod, hiddenStems, certain: true, alternatives: [],
  };
}

/**
 * ShouXingUtil.qiAccurate applies +1/3 day (UTC+8), not KST.
 * Add exactly one hour before converting its Julian days to civil dates.
 * Annual jie positions 2,4,...24 come from LunarUtil.JIE_QI_IN_USE.
 * Sources: https://github.com/6tail/lunar-typescript/blob/master/src/lib/ShouXingUtil.ts
 *          https://github.com/6tail/lunar-typescript/blob/master/src/lib/LunarYear.ts
 * This is model precision, not a claim of certified second-level astronomical accuracy.
 */
export function getSolarTerms(year: number): SolarTerm[] {
  if (!Number.isInteger(year) || year < SAJU_MIN_YEAR - 1 || year > SAJU_MAX_YEAR + 1) {
    throw new RangeError('절기 조회 범위를 벗어났습니다.');
  }
  let cached = termCache.get(year);
  if (!cached) {
    const julianDays = LunarYear.fromYear(year).getJieQiJulianDays();
    cached = JIE_NAMES.map((name, index) => {
      const date = Solar.fromJulianDay(julianDays[2 + index * 2] + 1 / 24);
      return { name, hanja: JIE_HANJA[index], atKst: `${date.toYmdHms().replace(' ', 'T')}+09:00`, monthBranchIndex: (index + 1) % 12 };
    });
    termCache.set(year, cached);
  }
  // Never expose cache objects to the UI or consumers.
  return cached.map(term => ({ ...term }));
}

function validateInput(input: SajuInput): void {
  const errors: SajuValidationIssue[] = [];
  if (!Number.isInteger(input.year) || input.year < SAJU_MIN_YEAR || input.year > SAJU_MAX_YEAR) errors.push({ field: 'year', message: '출생 연도는 1900년부터 2050년까지 입력해 주세요.' });
  if (!Number.isInteger(input.month) || input.month < 1 || input.month > 12) errors.push({ field: 'month', message: '월은 1부터 12까지 입력해 주세요.' });
  if (!Number.isInteger(input.day) || input.day < 1 || input.day > 31) errors.push({ field: 'day', message: '날짜를 올바르게 입력해 주세요.' });
  if (input.calendar !== 'solar' && input.calendar !== 'lunar') errors.push({ field: 'calendar', message: '양력 또는 음력을 선택해 주세요.' });
  if (typeof input.leapMonth !== 'boolean' || (input.calendar === 'solar' && input.leapMonth)) errors.push({ field: 'leapMonth', message: '윤달은 음력 생일에서만 선택할 수 있습니다.' });
  if (input.dayBoundary !== 'midnight' && input.dayBoundary !== 'zi') errors.push({ field: 'dayBoundary', message: '날짜 변경 기준을 선택해 주세요.' });
  if ((input.hour === null) !== (input.minute === null)) errors.push({ field: 'hour', message: '태어난 시와 분을 함께 입력하거나, 시간 모름을 선택해 주세요.' });
  if (input.hour !== null && (!Number.isInteger(input.hour) || input.hour < 0 || input.hour > 23)) errors.push({ field: 'hour', message: '시는 0부터 23까지 입력해 주세요.' });
  if (input.minute !== null && (!Number.isInteger(input.minute) || input.minute < 0 || input.minute > 59)) errors.push({ field: 'minute', message: '분은 0부터 59까지 입력해 주세요.' });
  if (errors.length) throw new SajuValidationError(errors);
}

interface MomentPillars {
  pillars: SajuResult['pillars'];
  previousJie: SolarTerm;
  nextJie: SolarTerm;
}

function pillarsAt(
  date: CalendarDate, hour: number, minute: number, dayBoundary: SajuInput['dayBoundary'],
  dayStem: number, dayBranch: number, terms: SolarTerm[], ipchun: SolarTerm,
  second = 0,
): MomentPillars {
  const stamp = localStamp(date, hour, minute, second);
  // ISO timestamps share the same explicit offset and width, making lexical comparison chronological.
  const cycleYear = stamp < ipchun.atKst ? date.year - 1 : date.year;
  const yearStem = mod(cycleYear - 4, 10);
  const yearBranch = mod(cycleYear - 4, 12);
  const nextIndex = terms.findIndex(term => term.atKst > stamp);
  if (nextIndex < 1) throw new Error('절입 구간을 계산할 수 없습니다.');
  const previousJie = terms[nextIndex - 1];
  const nextJie = terms[nextIndex];
  const monthBranch = previousJie.monthBranchIndex;
  const monthStem = mod((yearStem % 5 + 1) * 2 + mod(monthBranch - 2, 12), 10);
  const advanceDay = dayBoundary === 'zi' && hour === 23 ? 1 : 0;
  const effectiveDayStem = mod(dayStem + advanceDay, 10);
  const effectiveDayBranch = mod(dayBranch + advanceDay, 12);
  const hourBranch = Math.floor((hour + 1) / 2) % 12;
  // Apply the chosen day policy to BOTH the day pillar and the hour stem at 23:xx.
  // We deliberately do not use lunar-typescript's hour pillar, which always uses its zi-day index.
  const hourStem = (effectiveDayStem % 5 * 2 + hourBranch) % 10;
  return {
    pillars: {
      year: makePillar('year', yearStem, yearBranch, effectiveDayStem),
      month: makePillar('month', monthStem, monthBranch, effectiveDayStem),
      day: makePillar('day', effectiveDayStem, effectiveDayBranch, effectiveDayStem),
      hour: makePillar('hour', hourStem, hourBranch, effectiveDayStem),
    }, previousJie, nextJie,
  };
}

/** Offline calendar calculation. It makes no predictions and sends/stores no birth information. */
export function calculateSaju(input: SajuInput): SajuResult {
  validateInput(input);
  const calendar = new KoreanLunarCalendar();
  const valid = input.calendar === 'solar'
    ? calendar.setSolarDate(input.year, input.month, input.day)
    : calendar.setLunarDate(input.year, input.month, input.day, input.leapMonth);
  if (!valid) throw new SajuValidationError([{ field: 'date', message: input.calendar === 'lunar' ? '존재하지 않는 음력 날짜·윤달이거나 지원 범위를 벗어났습니다. 음력은 2050년 11월 18일까지 지원합니다.' : '존재하지 않는 양력 날짜입니다. 월과 일을 확인해 주세요.' }]);

  const solar = calendar.getSolarCalendar();
  const lunar = calendar.getLunarCalendar();
  const solarDate: CalendarDate = { year: solar.year, month: solar.month, day: solar.day };
  if (solar.year < SAJU_MIN_YEAR || solar.year > SAJU_MAX_YEAR) throw new SajuValidationError([{ field: 'date', message: '양력으로 환산한 날짜가 지원 범위(1900~2050년)를 벗어났습니다.' }]);
  const lunarDate = { year: lunar.year, month: lunar.month, day: lunar.day, leapMonth: lunar.intercalation === true };
  const indices = calendar.getGapJaIndex();
  const yearTerms = getSolarTerms(solar.year);
  const ipchun = yearTerms[1];
  const terms = [...getSolarTerms(solar.year - 1), ...yearTerms, ...getSolarTerms(solar.year + 1)];
  const timeKnown = input.hour !== null;
  const base = pillarsAt(solarDate, input.hour ?? 0, input.minute ?? 0, input.dayBoundary, indices.cheongan.day, indices.ganji.day, terms, ipchun);
  const unresolvedPillars: PillarKey[] = [];
  const warnings: SajuWarning[] = [];

  if (!timeKnown) {
    base.pillars.hour = null;
    warnings.push({ code: 'unknown-time', message: '태어난 시간을 몰라 시주는 계산하지 않았습니다. 오행은 년·월·일의 6글자만 집계합니다.' });
    // Compare the known date's interval endpoints. With at most one jie per civil day and the
    // optional 23:00 day boundary, these cover every possible year/month/day ganji on that date.
    const end = pillarsAt(solarDate, 23, 59, input.dayBoundary, indices.cheongan.day, indices.ganji.day, terms, ipchun, 59);
    for (const key of ['year', 'month', 'day'] as const) {
      const first = base.pillars[key];
      const last = end.pillars[key];
      if (first.hanja !== last.hanja) {
        first.certain = false;
        first.alternatives = [{ korean: first.korean, hanja: first.hanja }, { korean: last.korean, hanja: last.hanja }];
        unresolvedPillars.push(key);
      }
    }
    if (unresolvedPillars.length) warnings.push({ code: 'uncertain-boundary', message: '입춘·절입 또는 23시 날짜 경계 때문에 태어난 시간에 따라 원국이 달라집니다. 표시된 값은 당일 시작 구간의 참고값이며, 풀이를 보류합니다.' });
  } else {
    const birthMillis = Date.parse(localStamp(solarDate, input.hour!, input.minute!));
    const nearTerm = terms.find(term => Math.abs(Date.parse(term.atKst) - birthMillis) <= 120_000);
    if (nearTerm) warnings.push({ code: 'near-solar-term', message: `${nearTerm.name} 절입 전후 2분 안에 있습니다. 입력 초는 00초로 처리하며, 출생 기록과 역서의 반올림·천문 모델 차이에 따라 원국이 달라질 수 있습니다.` });
  }
  if (solar.year <= 1988) warnings.push({ code: 'historical-time', message: '과거 한국의 UTC+08:30 표준시·서머타임은 자동 보정하지 않습니다. 기록 시각을 UTC+09:00으로 환산해 입력해야 합니다.' });
  if (input.dayBoundary === 'zi') warnings.push({ code: 'zi-day-boundary', message: '23:00부터 다음 일진을 적용하고, 시주의 천간도 바뀐 일간을 기준으로 계산했습니다.' });

  const elements: Record<Element, number> = { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 };
  for (const pillar of Object.values(base.pillars)) {
    if (!pillar) continue;
    elements[pillar.stem.element]++;
    elements[pillar.branch.element]++;
  }
  return {
    input: { ...input }, solarDate, lunarDate, pillars: base.pillars, dayMaster: { ...base.pillars.day.stem },
    elements, elementTotal: timeKnown ? 8 : 6,
    metadata: {
      engineVersion: 'oneultool-saju/1.0.0', timezone: 'UTC+09:00', timeKnown,
      birthAtKst: timeKnown ? localStamp(solarDate, input.hour!, input.minute!) : null,
      dayBoundary: input.dayBoundary, yearBoundary: 'ipchun-instant', monthBoundary: '12-jie-instant',
      solarTermSource: 'lunar-typescript@1.8.6 (ShouXing 계산, UTC+08 → UTC+09)',
      lunarSource: 'korean-lunar-calendar@0.4.0 (한국 음력 변환표)',
      ipchun, previousJie: base.previousJie, nextJie: base.nextJie, unresolvedPillars,
      interpretationAllowed: unresolvedPillars.length === 0, warnings,
      policies: [
        '입력 시각은 고정 UTC+09:00입니다. 출생지 경도·진태양시·과거 표준시·서머타임을 보정하지 않습니다.',
        '양력 1900~2050년을 지원합니다. 음력은 한국 음력표로 변환하며 2050년 11월 18일까지 지원합니다.',
        '년주는 입춘의 계산 시각, 월주는 12절의 계산 시각에 바뀝니다. 음력 초하루나 고정 날짜를 경계로 쓰지 않습니다.',
        input.dayBoundary === 'midnight' ? '일주와 시주의 일간 기준은 자정(00:00)에 바뀝니다. 23시는 당일 일간을 사용합니다.' : '일주와 시주의 일간 기준은 자초(23:00)에 함께 바뀝니다.',
        '시는 23~01시 자시, 01~03시 축시처럼 2시간 단위입니다. 입력하지 않은 초는 00초입니다.',
        '오행은 겉글자 8개(시각 미상은 6개)를 동일하게 셉니다. 지장간·월령의 가중치는 이 분포에 섞지 않습니다.',
        '지장간은 본기 우선의 통상 표를 사용합니다. 지장간 구성과 자시 해석은 학파에 따라 다를 수 있습니다.',
        '절입 초는 천문 모델의 계산값입니다. 경계 부근의 초 단위 정확도나 모든 역서와의 일치를 보증하지 않습니다.',
      ],
    },
  };
}
